import axios from 'axios';

export const API_BASE_URL = (process.env.REACT_APP_API_URL || 'https://threecr-sen-1-273g.onrender.com').replace(/\/$/, '');

const TOKEN_KEY = 'token';
const USER_KEY = 'sessionUser';
// Older screens still read these individual keys to adapt what they display.
const PROFILE_KEYS = ['userRole', 'userAccessLevel', 'userName', 'userService'];

export const getToken = () => localStorage.getItem(TOKEN_KEY);

export const getSessionUser = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY)) || null;
  } catch (error) {
    return null;
  }
};

const readTokenPayload = (token) => {
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(window.atob(part), (char) => char.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch (error) {
    return null;
  }
};

// Client-side check only (the server verifies the signature on every request):
// rejects tokens from the old login and tokens past their 10 h lifetime.
export const hasValidSession = () => {
  const token = getToken();
  if (!token) return false;
  const payload = readTokenPayload(token);
  return Boolean(payload && payload.typ === 'session' && payload.exp * 1000 > Date.now());
};

// Reloads the account from the server (permissions changed by the administrator, or a
// session opened before they were delivered with it).
export const refreshSessionUser = async () => {
  if (!hasValidSession()) return;
  const response = await fetch(`${API_BASE_URL}/api/auth/me`);
  if (!response.ok) return;
  const { user } = await response.json();
  // An older server answers without `rights`: keep what was saved at login.
  if (!user || !user.rights || !hasValidSession()) return;
  saveSession({ token: getToken(), user: { ...getSessionUser(), ...user } });
};

// Fired when the saved account changes, so that open screens show the new permissions.
export const SESSION_USER_EVENT = 'servicesfann:session-user';

export const saveSession = ({ token, user }) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  localStorage.setItem('userRole', user.role);
  localStorage.setItem('userAccessLevel', user.accessLevel);
  localStorage.setItem('userName', user.name);
  localStorage.setItem('userService', user.service);
  window.dispatchEvent(new Event(SESSION_USER_EVENT));
};

// Grants for sensitive spaces (payments, archives), obtained with the personal access
// code. Kept per browser tab: closing the tab locks the spaces again.
const GRANTS_KEY = 'areaGrants';

const readGrants = () => {
  try {
    return JSON.parse(sessionStorage.getItem(GRANTS_KEY)) || {};
  } catch (error) {
    return {};
  }
};

export const getAreaGrant = (area) => {
  const token = readGrants()[area];
  const payload = token && readTokenPayload(token);
  const user = getSessionUser();
  return payload && payload.exp * 1000 > Date.now() && user && payload.sub === user.id ? token : null;
};

export const saveAreaGrant = (area, token) => {
  sessionStorage.setItem(GRANTS_KEY, JSON.stringify({ ...readGrants(), [area]: token }));
};

const clearAreaGrant = (area) => {
  const grants = readGrants();
  delete grants[area];
  sessionStorage.setItem(GRANTS_KEY, JSON.stringify(grants));
};

const activeGrants = () => Object.keys(readGrants()).map(getAreaGrant).filter(Boolean);

const sessionClearedListeners = new Set();

// Lets other modules (the Firebase sign-in) end with the session.
export const onSessionCleared = (listener) => {
  sessionClearedListeners.add(listener);
};

export const clearSession = () => {
  [TOKEN_KEY, USER_KEY, ...PROFILE_KEYS, 'isAuthenticated', 'lastPath'].forEach((key) =>
    localStorage.removeItem(key)
  );
  sessionStorage.removeItem(GRANTS_KEY);
  sessionClearedListeners.forEach((listener) => listener());
};

// The space was locked again on the server (code changed, grant expired): ask for the code.
const relockArea = (area) => {
  clearAreaGrant(area);
  window.location.reload();
};

export const endSession = (reason) => {
  clearSession();
  if (window.location.pathname !== '/auth') {
    window.location.assign(reason ? `/auth?raison=${reason}` : '/auth');
  }
};

const isApiUrl = (url) => typeof url === 'string' && url.startsWith(API_BASE_URL);
// Wrong credentials answer 401 on these routes: that is not an expired session.
const isLoginUrl = (url) => url.startsWith(`${API_BASE_URL}/api/auth/`);

let installed = false;

export const installAuthInterceptors = () => {
  if (installed) return;
  installed = true;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input && input.url;
    if (!isApiUrl(url)) return nativeFetch(input, init);

    const headers = new Headers(init.headers || undefined);
    const token = getToken();
    if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
    const grants = activeGrants();
    if (grants.length && !headers.has('X-Area-Grants')) headers.set('X-Area-Grants', grants.join(','));

    const response = await nativeFetch(input, { ...init, headers });
    if (response.status === 401 && !isLoginUrl(url)) endSession('expiree');
    if (response.status === 403) {
      const body = await response.clone().json().catch(() => ({}));
      if (body.code === 'AREA_LOCKED' && getAreaGrant(body.area)) relockArea(body.area);
    }
    return response;
  };

  axios.interceptors.request.use((config) => {
    const url = `${config.baseURL || ''}${config.url || ''}`;
    if (!isApiUrl(url)) return config;
    const token = getToken();
    if (token && !config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`;
    const grants = activeGrants();
    if (grants.length && !config.headers['X-Area-Grants']) config.headers['X-Area-Grants'] = grants.join(',');
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const url = error.config ? `${error.config.baseURL || ''}${error.config.url || ''}` : '';
      const status = error.response && error.response.status;
      const body = (error.response && error.response.data) || {};
      if (status === 401 && isApiUrl(url) && !isLoginUrl(url)) endSession('expiree');
      if (status === 403 && body.code === 'AREA_LOCKED' && getAreaGrant(body.area)) relockArea(body.area);
      return Promise.reject(error);
    }
  );
};
